/**
 * Patron enrollment page.
 */

import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useForm,
  type FieldErrors,
  type Resolver,
  type UseFormRegisterReturn,
} from "react-hook-form";
import { z } from "zod";
import { useCreatePatron, useLocations, usePatronCategories } from "@/api/hooks";
import { ApiClientError } from "@/api/client";
import type { CreatePatronInput, Location, PatronGender } from "@/api/types";

const GENDER_LABELS: Record<PatronGender, string> = {
  not_specified: "Non précisé",
  female: "Femme",
  male: "Homme",
  other: "Autre",
};

// Mirrors the PatronResource constraints so most errors surface before the request.
const patronSchema = z.object({
  barcode: z
    .string()
    .trim()
    .min(4, "Le code-barres doit contenir au moins 4 caractères")
    .max(50, "Le code-barres ne doit pas dépasser 50 caractères"),
  firstName: z.string().trim().min(1, "Le prénom est requis").max(100, "100 caractères maximum"),
  lastName: z.string().trim().min(1, "Le nom est requis").max(100, "100 caractères maximum"),
  categoryId: z.string().min(1, "Choisissez une catégorie"),
  homeLocationId: z.string().min(1, "Choisissez une bibliothèque de rattachement"),
  email: z.union([z.literal(""), z.email({ error: "Adresse e-mail invalide" })]),
  phone: z.string().trim(),
  mobilePhone: z.string().trim(),
  birthDate: z.string(),
  gender: z.enum(["not_specified", "female", "male", "other"]),
  address: z.string().trim(),
  address2: z.string().trim(),
  postalCode: z.string().trim(),
  city: z.string().trim(),
  expirationDate: z.string(),
  notes: z.string().trim(),
  emailNotifications: z.boolean(),
  smsNotifications: z.boolean(),
});

type PatronFormData = z.infer<typeof patronSchema>;

// @hookform/resolvers 3.x predates zod 4 and throws instead of reporting issues, so the schema
// is applied directly.
const patronResolver: Resolver<PatronFormData> = (values) => {
  const result = patronSchema.safeParse(values);
  if (result.success) {
    return { values: result.data, errors: {} };
  }

  const errors: FieldErrors<PatronFormData> = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof PatronFormData;
    errors[field] ??= { type: issue.code, message: issue.message };
  }

  return { values: {}, errors };
};

const FIELD_NAMES = Object.keys(patronSchema.shape) as Array<keyof PatronFormData>;

const DEFAULT_VALUES: PatronFormData = {
  barcode: "",
  firstName: "",
  lastName: "",
  categoryId: "",
  homeLocationId: "",
  email: "",
  phone: "",
  mobilePhone: "",
  birthDate: "",
  gender: "not_specified",
  address: "",
  address2: "",
  postalCode: "",
  city: "",
  expirationDate: "",
  notes: "",
  emailNotifications: true,
  smsNotifications: false,
};

/** Optional fields left blank are omitted rather than sent as empty strings. */
const OPTIONAL_TEXT_FIELDS = [
  "email",
  "phone",
  "mobilePhone",
  "birthDate",
  "address",
  "address2",
  "postalCode",
  "city",
  "expirationDate",
  "notes",
] as const satisfies ReadonlyArray<keyof PatronFormData & keyof CreatePatronInput>;

function toInput(data: PatronFormData): CreatePatronInput {
  const input: CreatePatronInput = {
    barcode: data.barcode,
    firstName: data.firstName,
    lastName: data.lastName,
    categoryId: data.categoryId,
    homeLocationId: data.homeLocationId,
    gender: data.gender,
    emailNotifications: data.emailNotifications,
    smsNotifications: data.smsNotifications,
  };

  for (const field of OPTIONAL_TEXT_FIELDS) {
    if (data[field] !== "") {
      input[field] = data[field];
    }
  }

  return input;
}

/** Patrons are attached to a library or one of its branches, not to a network or a shelf section. */
function isHomeLocation(location: Location): boolean {
  return location.active && (location.type === "library" || location.type === "branch");
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none aria-[invalid=true]:border-red-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:aria-[invalid=true]:border-red-400";

export function PatronCreatePage() {
  const navigate = useNavigate();
  const createPatron = useCreatePatron();
  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = usePatronCategories();
  const { data: locations, isLoading: locationsLoading, error: locationsError } = useLocations();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PatronFormData>({
    resolver: patronResolver,
    defaultValues: DEFAULT_VALUES,
  });

  const activeCategories = (categories ?? []).filter((c) => c.active);
  const homeLocations = (locations ?? []).filter(isHomeLocation);
  const referencesLoading = categoriesLoading || locationsLoading;

  // Enrollment needs both references; say which one is missing instead of showing empty lists.
  const referenceProblems: string[] = [];
  if (categoriesError || locationsError) {
    referenceProblems.push("Impossible de charger les catégories ou les bibliothèques.");
  } else if (!referencesLoading) {
    if (activeCategories.length === 0) {
      referenceProblems.push("Aucune catégorie de lecteur active n'est configurée.");
    }
    if (homeLocations.length === 0) {
      referenceProblems.push("Aucune bibliothèque ou antenne active n'est configurée.");
    }
  }
  const canSubmit = !referencesLoading && referenceProblems.length === 0;

  const onSubmit = async (data: PatronFormData) => {
    setFormError(null);

    try {
      const patron = await createPatron.mutateAsync(toInput(data));
      void navigate(`/circulation/patrons/${patron.id}`);
    } catch (err) {
      if (!(err instanceof ApiClientError)) {
        setFormError("Une erreur est survenue lors de l'enregistrement");
        return;
      }

      // Attach server-side violations to their fields; anything unmapped goes to the banner.
      let unmapped = false;
      for (const violation of err.data?.violations ?? []) {
        const field = FIELD_NAMES.find((name) => name === violation.propertyPath);
        if (field) {
          setError(field, { message: violation.message });
        } else {
          unmapped = true;
        }
      }

      if (err.status === 409) {
        setError("barcode", { message: err.message });
      } else if (unmapped || !err.data?.violations?.length) {
        setFormError(err.message);
      }
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/circulation/patrons"
          aria-label="Retour à la liste des lecteurs"
          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:bg-gray-800"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Nouveau lecteur</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Les champs marqués d'un astérisque sont obligatoires.
          </p>
        </div>
      </div>

      {referenceProblems.length > 0 && (
        <div
          role="status"
          className="space-y-1 rounded-lg bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
        >
          {referenceProblems.map((problem) => (
            <p key={problem}>{problem}</p>
          ))}
          <p>Un administrateur doit les créer avant de pouvoir inscrire un lecteur.</p>
        </div>
      )}

      {formError && (
        <div
          role="alert"
          className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
        >
          {formError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        <Section title="Identité">
          <Field
            id="barcode"
            label="Code-barres de la carte"
            required
            error={errors.barcode?.message}
          >
            <input
              id="barcode"
              autoComplete="off"
              autoFocus
              className={inputClass}
              {...withA11y(register("barcode"), "barcode", errors.barcode?.message)}
            />
          </Field>
          <Field id="gender" label="Genre" error={errors.gender?.message}>
            <select id="gender" className={inputClass} {...register("gender")}>
              {(Object.keys(GENDER_LABELS) as PatronGender[]).map((gender) => (
                <option key={gender} value={gender}>
                  {GENDER_LABELS[gender]}
                </option>
              ))}
            </select>
          </Field>
          <Field id="lastName" label="Nom" required error={errors.lastName?.message}>
            <input
              id="lastName"
              autoComplete="off"
              className={inputClass}
              {...withA11y(register("lastName"), "lastName", errors.lastName?.message)}
            />
          </Field>
          <Field id="firstName" label="Prénom" required error={errors.firstName?.message}>
            <input
              id="firstName"
              autoComplete="off"
              className={inputClass}
              {...withA11y(register("firstName"), "firstName", errors.firstName?.message)}
            />
          </Field>
          <Field id="birthDate" label="Date de naissance" error={errors.birthDate?.message}>
            <input id="birthDate" type="date" className={inputClass} {...register("birthDate")} />
          </Field>
        </Section>

        <Section title="Inscription">
          <Field id="categoryId" label="Catégorie" required error={errors.categoryId?.message}>
            <select
              id="categoryId"
              disabled={referencesLoading}
              className={inputClass}
              {...withA11y(register("categoryId"), "categoryId", errors.categoryId?.message)}
            >
              <option value="">{categoriesLoading ? "Chargement…" : "Choisir…"}</option>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            id="homeLocationId"
            label="Bibliothèque de rattachement"
            required
            error={errors.homeLocationId?.message}
          >
            <select
              id="homeLocationId"
              disabled={referencesLoading}
              className={inputClass}
              {...withA11y(
                register("homeLocationId"),
                "homeLocationId",
                errors.homeLocationId?.message
              )}
            >
              <option value="">{locationsLoading ? "Chargement…" : "Choisir…"}</option>
              {homeLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            id="expirationDate"
            label="Fin de validité de la carte"
            hint="Laisser vide pour une carte sans échéance."
            error={errors.expirationDate?.message}
          >
            <input
              id="expirationDate"
              type="date"
              className={inputClass}
              {...register("expirationDate")}
            />
          </Field>
        </Section>

        <Section title="Contact">
          <Field id="email" label="E-mail" error={errors.email?.message}>
            <input
              id="email"
              type="email"
              autoComplete="off"
              className={inputClass}
              {...withA11y(register("email"), "email", errors.email?.message, false)}
            />
          </Field>
          <Field id="phone" label="Téléphone" error={errors.phone?.message}>
            <input
              id="phone"
              type="tel"
              autoComplete="off"
              className={inputClass}
              {...register("phone")}
            />
          </Field>
          <Field id="mobilePhone" label="Mobile" error={errors.mobilePhone?.message}>
            <input
              id="mobilePhone"
              type="tel"
              autoComplete="off"
              className={inputClass}
              {...register("mobilePhone")}
            />
          </Field>
          <fieldset className="space-y-2 sm:col-span-2">
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Notifications
            </legend>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                {...register("emailNotifications")}
              />
              Par e-mail
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                {...register("smsNotifications")}
              />
              Par SMS
            </label>
          </fieldset>
        </Section>

        <Section title="Adresse">
          <Field id="address" label="Adresse" wide error={errors.address?.message}>
            <input
              id="address"
              autoComplete="off"
              className={inputClass}
              {...register("address")}
            />
          </Field>
          <Field id="address2" label="Complément d'adresse" wide error={errors.address2?.message}>
            <input
              id="address2"
              autoComplete="off"
              className={inputClass}
              {...register("address2")}
            />
          </Field>
          <Field id="postalCode" label="Code postal" error={errors.postalCode?.message}>
            <input
              id="postalCode"
              autoComplete="off"
              className={inputClass}
              {...register("postalCode")}
            />
          </Field>
          <Field id="city" label="Ville" error={errors.city?.message}>
            <input id="city" autoComplete="off" className={inputClass} {...register("city")} />
          </Field>
        </Section>

        <Section title="Notes internes">
          <Field id="notes" label="Notes" wide error={errors.notes?.message}>
            <textarea id="notes" rows={3} className={inputClass} {...register("notes")} />
          </Field>
        </Section>

        <div className="flex justify-end gap-3">
          <Link
            to="/circulation/patrons"
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || !canSubmit}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:opacity-60"
          >
            {isSubmitting ? "Enregistrement…" : "Créer le lecteur"}
          </button>
        </div>
      </form>
    </div>
  );
}

function withA11y(
  registration: UseFormRegisterReturn,
  id: string,
  error: string | undefined,
  required = true
) {
  return {
    ...registration,
    "aria-required": required || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? `${id}-error` : undefined,
  };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
      <legend className="px-1 text-sm font-semibold text-gray-900 dark:text-white">{title}</legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  wide?: boolean;
  hint?: string;
  error: string | undefined;
  children: ReactNode;
}

function Field({ id, label, required, wide, hint, error, children }: FieldProps) {
  return (
    <div className={`space-y-1 ${wide ? "sm:col-span-2" : ""}`}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && (
          <span className="ml-0.5 text-red-600 dark:text-red-400" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
